import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Check, Loader2, X } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch {
        setStatus("failed"); return;
      }
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-5 noise-mask" data-testid="payment-success-page">
      <div className="w-full max-w-md border border-[#222] bg-matte p-10 text-center">
        {status === "checking" && (
          <><Loader2 className="animate-spin text-neon mx-auto mb-6" size={40} />
            <h1 className="font-display uppercase text-3xl tracking-tight">Confirming Payment…</h1>
            <p className="mt-3 font-mono text-sm text-[#9CA3AF]">Hang tight while we verify your order.</p></>
        )}
        {status === "paid" && (
          <><div className="w-16 h-16 bg-neon flex items-center justify-center mx-auto mb-6"><Check size={32} className="text-void" /></div>
            <h1 className="font-display uppercase text-4xl tracking-tight">Order Confirmed</h1>
            <p className="mt-3 font-mono text-sm text-[#9CA3AF]">Thanks for your order — we'll be in touch about shipping shortly.</p></>
        )}
        {status === "failed" && (
          <><div className="w-16 h-16 bg-panel border border-[#333] flex items-center justify-center mx-auto mb-6"><X size={32} className="text-[#9CA3AF]" /></div>
            <h1 className="font-display uppercase text-3xl tracking-tight">Payment Not Confirmed</h1>
            <p className="mt-3 font-mono text-sm text-[#9CA3AF]">We couldn't confirm this payment. If you were charged, contact us.</p></>
        )}
        <Link to="/performance" data-testid="back-to-store" className="inline-block mt-8 bg-neon text-void font-bold uppercase tracking-[0.2em] text-xs py-4 px-8 hover:bg-[#FAFAFA] transition-colors">
          Back To Store
        </Link>
      </div>
    </div>
  );
}
